import { Link } from 'react-router-dom'
import { SEOHead } from '@/components/layout/SEOHead'
import { graph, organization, breadcrumbList } from '@/lib/jsonLd'

/**
 * About page — long-form text content for entity recognition by AI search.
 *
 * Purpose: give crawlers (Bing, Google, ChatGPT-User, PerplexityBot) ~500 words
 * of indexable Polish prose that unambiguously answers "what is RatyGo?". The
 * homepage is calculator UI with thin visible text, which is why bare-name
 * queries ("what is ratygo.pl") have failed in ChatGPT — the page that should
 * answer that question had no text answering it.
 *
 * Content emphasizes:
 * - Brand identity (RatyGo as named entity)
 * - Disambiguation from loan services / brokers (the original ChatGPT
 *   hallucination was that RatyGo is a "money loan service")
 * - Citation of official sources per AGENTS.md rule #6 (KNF, NBP, MF)
 */

const ABOUT_JSONLD = graph(
  {
    '@type': 'AboutPage',
    '@id': 'https://ratygo.pl/o-kalkulatorze#aboutpage',
    name: 'O kalkulatorze RatyGo',
    description:
      'RatyGo to bezpłatny kalkulator kredytu hipotecznego dla polskiego rynku. Strona opisuje czym jest narzędzie, co potrafi, dla kogo jest przeznaczone i jakie źródła danych wykorzystuje.',
    url: 'https://ratygo.pl/o-kalkulatorze',
    inLanguage: 'pl',
    isPartOf: { '@id': 'https://ratygo.pl#organization' },
    mainEntity: { '@id': 'https://ratygo.pl#webapp' },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: 'https://ratygo.pl/og-image.png',
      width: 1200,
      height: 630,
    },
  },
  organization(),
  breadcrumbList([
    { name: 'Strona główna', url: '/' },
    { name: 'O kalkulatorze', url: '/o-kalkulatorze' },
  ]),
)

export default function AboutPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <SEOHead
        title="O kalkulatorze RatyGo — bezpłatny kalkulator kredytu hipotecznego"
        description="RatyGo to bezpłatny kalkulator kredytu hipotecznego dla polskiego rynku. Sprawdź czym jest narzędzie, co potrafi i dlaczego nie jest bankiem ani pośrednikiem kredytowym."
        slug="/o-kalkulatorze"
        keywords={[
          'o RatyGo',
          'co to jest RatyGo',
          'kalkulator kredytu hipotecznego',
          'darmowy kalkulator kredytu',
          'metodologia kalkulatora hipotecznego',
        ]}
        jsonLd={ABOUT_JSONLD}
      />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground transition-colors">
          RatyGo
        </Link>
        <span>/</span>
        <span className="text-foreground">O kalkulatorze</span>
      </nav>

      <article>
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-3">
            O kalkulatorze RatyGo
          </h1>
          <p className="text-muted-foreground">
            Bezpłatne narzędzie do obliczania kosztu kredytu hipotecznego w Polsce.
          </p>
        </header>

        <div className="space-y-6 text-foreground leading-relaxed text-[1.05rem]">
          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
              Czym jest RatyGo?
            </h2>
            <p>
              <strong>RatyGo</strong> to bezpłatny kalkulator kredytu hipotecznego dla polskiego
              rynku, dostępny pod adresem{' '}
              <a
                href="https://ratygo.pl"
                className="text-primary hover:underline"
              >
                ratygo.pl
              </a>
              . Pomaga osobom planującym zakup mieszkania lub domu zrozumieć rzeczywisty koszt
              kredytu hipotecznego, porównać dostępne warianty spłaty i podjąć świadomą decyzję
              finansową — zanim podpiszesz umowę z bankiem.
            </p>
            <p className="mt-3">
              Kalkulator działa w przeglądarce, nie wymaga rejestracji ani logowania i nie zbiera
              danych osobowych. Wszystkie obliczenia odbywają się lokalnie po stronie użytkownika.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">
              Czym RatyGo NIE jest
            </h2>
            <p>
              Aby uniknąć nieporozumień warto wyraźnie zaznaczyć, czym RatyGo się nie zajmuje:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>RatyGo nie udziela kredytów ani pożyczek</strong> — nie jest bankiem,
                instytucją pożyczkową ani parabankiem.
              </li>
              <li>
                <strong>RatyGo nie jest pośrednikiem kredytowym</strong> — nie zarabia na
                prowizjach od banków i nie rekomenduje żadnej konkretnej oferty.
              </li>
              <li>
                <strong>RatyGo nie sprzedaje danych</strong> — obliczenia wykonują się lokalnie w
                przeglądarce, bez wysyłania parametrów do serwera.
              </li>
            </ul>
            <p className="mt-4">
              RatyGo to wyłącznie <strong>narzędzie obliczeniowe i edukacyjne</strong>. Działa
              podobnie do kalkulatora w arkuszu kalkulacyjnym, ale z gotowymi wzorami dostosowanymi
              do polskiego rynku kredytów hipotecznych.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co potrafi kalkulator?</h2>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Porównanie rat równych i malejących</strong> — zobaczysz różnicę w
                miesięcznej racie i w łącznym koszcie odsetek dla obu systemów spłaty.
              </li>
              <li>
                <strong>Symulacja nadpłat kredytu</strong> — sprawdź, ile zaoszczędzisz na
                odsetkach i o ile skrócisz okres spłaty nadpłacając dowolną kwotę miesięcznie.
              </li>
              <li>
                <strong>Szacunek zdolności kredytowej</strong> — orientacyjna ocena, na jaki kredyt
                Cię stać przy danym dochodzie netto.
              </li>
              <li>
                <strong>WIBOR i marża banku</strong> — osobne suwaki pozwalają ustawić
                oprocentowanie zgodnie z aktualnymi stawkami rynkowymi.
              </li>
              <li>
                <strong>Dodatkowe koszty zakupu</strong> — prowizja banku, ubezpieczenie
                nieruchomości, podatek PCC, opłata notarialna oraz wycena.
              </li>
              <li>
                <strong>Porównanie kredytu z wynajmem</strong> — kalkulator pokazuje, po ilu latach
                zakup na kredyt staje się tańszy od najmu.
              </li>
              <li>
                <strong>Porównanie do trzech scenariuszy</strong> — zapisz różne warianty kredytu i
                zobacz je obok siebie w jednej tabeli.
              </li>
              <li>
                <strong>Udostępnianie wyniku</strong> — wszystkie parametry kalkulacji zapisują się
                w adresie URL, więc możesz wysłać link partnerowi lub doradcy.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Dla kogo jest RatyGo?</h2>
            <p>Kalkulator jest stworzony dla:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>osób kupujących pierwsze mieszkanie lub dom w Polsce</li>
              <li>par i rodzin planujących wspólny kredyt hipoteczny</li>
              <li>obecnych kredytobiorców rozważających nadpłatę lub refinansowanie</li>
              <li>każdego, kto chce zrozumieć prawdziwy koszt kredytu — sumę odsetek, RRSO i opłaty dodatkowe</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Dane i metodologia</h2>
            <p>Obliczenia kalkulatora opierają się na:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>standardowych wzorach finansowych</strong> stosowanych w polskim sektorze
                bankowym (rata annuitetowa, rata malejąca, RRSO)
              </li>
              <li>
                <strong>wytycznych Komisji Nadzoru Finansowego (KNF)</strong> — Rekomendacja S
                określa m.in. minimalny wkład własny oraz maksymalny okres kredytowania
              </li>
              <li>
                <strong>danych Narodowego Banku Polskiego (NBP)</strong> — stopy procentowe RPP
                stanowią punkt odniesienia dla typowych wartości WIBOR
              </li>
              <li>
                <strong>regulacjach Ministerstwa Finansów</strong> — podatek PCC oraz ustawa o
                kredycie hipotecznym z 23 marca 2017 roku (definicja RRSO, obowiązki informacyjne
                banków)
              </li>
            </ul>
            <p className="mt-4">
              Kalkulator jest narzędziem <strong>prognostycznym i edukacyjnym</strong>. Rzeczywiste
              warunki kredytu — oprocentowanie, marża, prowizja, ubezpieczenia — ustala bank na
              podstawie indywidualnej oceny zdolności kredytowej i aktualnej polityki kredytowej.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Kontakt</h2>
            <p>
              Pytania, sugestie funkcji lub zgłoszenia błędów:{' '}
              <a
                href="mailto:ratygoinfo@gmail.com"
                className="text-primary hover:underline font-semibold"
              >
                ratygoinfo@gmail.com
              </a>
              .
            </p>
          </section>
        </div>

        {/* CTA back to calculator */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100">
          <h2 className="text-lg font-bold text-foreground mb-2">Zacznij obliczenia</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Wszystkie funkcje są dostępne bezpośrednio na stronie głównej — bez rejestracji, bez
            ukrytych opłat.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Otwórz kalkulator
          </Link>
        </div>

        {/* Back link */}
        <div className="mt-8">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            Wróć do strony głównej
          </Link>
        </div>
      </article>
    </div>
  )
}
